import type { SlateLeaf } from '@atlas/renderer';
import extractNodeListText from '@atlas/tool/box/slate/extractNodeListText';
import isHtmlElementNode from '@atlas/tool/box/dom/isHtmlElementNode';
import isListHtmlElement from '@atlas/tool/box/dom/isListHtmlElement';

export default function extractNodeTextRecursively(nodeList: NodeListOf<ChildNode>): SlateLeaf[] {
    return extract(nodeList, 0, null).map(({ shift, ...leaf }) => {
        if (!shift) return leaf;
        return {
            ...leaf,
            text: leaf.text.replace(/\n/g, `\n${new Array(shift * 4).fill(' ').join('')}`),
        };
    });
}

// #region Helpers

function extract(
    nodeList: NodeListOf<ChildNode>,
    shift: number,
    listIndex: number | string | null,
): Array<SlateLeaf & { shift?: number }> {
    const leaves = extractNodeListText(nodeList).map((leaf) => ({
        ...leaf,
        shift,
    }));

    if (!nodeList.length) return leaves;

    const firstNode = nodeList[0];
    firstNode.remove();

    if (!(firstNode instanceof HTMLElement)) {
        return [...leaves, ...extract(nodeList, shift, listIndex)];
    }

    if (isHtmlElementNode(firstNode, 'BR')) {
        return [...leaves, { text: '\n', shift }, ...extract(nodeList, shift, listIndex)];
    }

    const nestedPrefix: SlateLeaf[] =
        listIndex !== null && isHtmlElementNode(firstNode, 'LI')
            ? [
                  {
                      text: `${typeof listIndex === 'string' ? listIndex : `${++listIndex}.`} `,
                      bold: true,
                  },
              ]
            : [];

    const firstNodeIsListBlock = isListHtmlElement(firstNode);
    const nested = [
        ...nestedPrefix,
        ...extract(
            firstNode.childNodes,
            shift + (firstNodeIsListBlock ? 1 : 0),
            firstNodeIsListBlock ? (isHtmlElementNode(firstNode, 'OL') ? 0 : '-') : listIndex,
        ),
    ];
    const rest = extract(nodeList, shift, listIndex);

    return [
        ...leaves,
        ...(leaves.length && !leaves[leaves.length - 1].text.endsWith('\n')
            ? nested.length && !nested[0].text.startsWith('\n')
                ? [{ text: '\n', shift: shift + 1 }]
                : rest.length && !rest[0].text.startsWith('\n')
                ? [{ text: '\n', shift }]
                : []
            : []),
        ...nested,
        ...((leaves.length || nested.length) &&
        rest.length &&
        !(leaves[leaves.length - 1] || nested[nested.length - 1]).text.endsWith('\n') &&
        !rest[rest.length - 1].text.startsWith('\n')
            ? [{ text: '\n', shift }]
            : []),
        ...rest,
    ];
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('extractNodeTextRecursively', async () => {
        it.skip('extracts list with proper offsets', () => {
            const nodesList = new DOMParser().parseFromString(
                // <table> wrapper is needed to handle td/th/tr/tbody/thead/tfoot without wrapper tag
                // It creates empty table tag that is safe for parser
                '<p>Here are a few things you can do with tickets:\n  <ul>\n    <li><b>assign</b> this ticket to a teammate by hovering over the ticket and pressing "A".</li>\n    <li><b>prioritize</b> the ticket by hovering over the ticket and pressing "P".</li>\n    <li><b>close</b> the ticket by hovering over the ticket and pressing "C".</li>\n  </ul>\n</p>',
                'text/html',
            ).body.childNodes;
            expect(
                extractNodeTextRecursively(nodesList)
                    .map((l) => l.text)
                    .join('')
                    .split('\n'),
            ).toEqual([
                'Here are a few things you can do with tickets:',
                '  ',
                '- assign this ticket to a teammate by hovering over the ticket and pressing "A".',
                '- prioritize the ticket by hovering over the ticket and pressing "P".',
                '- close the ticket by hovering over the ticket and pressing "C."',
            ]);
        });
    });
}

// #endregion
